import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { filter, withLatestFrom } from 'rxjs/operators';

import {
  CertificateActionTypes,
  CertificateDetailsLoad,
  CertificateDetailsLoadSuccess,
  CertificateDetailsPatch,
  CertificateIssueMccs,
  CertificateIssueRfcc,
  CertificateIssueRfoc,
  CertificateMccIssueOptionsLoad,
  CertificateMccIssueOptionsLoadSuccess,
  CertificateMoveMccToHandover,
  CertificateRfccIssueOptionsLoad,
  CertificateRfccIssueOptionsLoadSuccess,
  CertificateRfocIssueOptionsLoad,
  CertificateRfocIssueOptionsLoadSuccess,
  CertificateSaveComment,
  CertificatesLoad,
  CertificatesLoadSuccess,
  CertificateUpdateMccAccept,
  CertificateUpdateMccSignature,
  CertificateUpdateRfccSignature,
  CertificateUpdateRfocSignature,
  DeleteMccFile,
  IssuePunchRoundMcCertificate,
  IssuePunchRoundMcCertificateSuccess,
  LoadPunchRoundCertificates,
  LoadPunchRoundCertificatesSuccess,
  LoadPunchRounds,
  UploadMccAttachment,
  SyncMccWithMips,
  SyncMccWithMipsSuccess,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentCpId, getCurrentProject } from '@completion/selectors';
import { CertificateService } from '@completion/services';
import { trackResource } from './resource-fetch.util';
import { HttpResponse } from '@angular/common/http';
import { Certificates, PunchRoundMcpView } from '@completion/models';
import { PunchRoundService } from '../../services/punch-round.service';
import { Observable } from 'rxjs';
import { Project } from '@completion/models';

@Injectable()
export class CertificateEffect {
  constructor(
    private actions$: Actions,
    private certificateService: CertificateService,
    private punchRoundService: PunchRoundService,
    private store: Store<State>,
    private toastr: NotificationService,
  ) {}

  loadCertificates$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificatesLoad>(CertificateActionTypes.CertificatesLoad),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource<Certificates>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'getCertificates',
        serviceArgs: ([_, currentProject, cpId]) => [currentProject.id, cpId],
        onSuccess: (response) => new CertificatesLoadSuccess(response),
      }),
    ),
  );

  issueMccs$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateIssueMccs>(CertificateActionTypes.CertificateIssueMccs),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource<HttpResponse<void>>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'issueMccs',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.data],
        onSuccess: () => null,
      }),
    ),
  );

  issueRfcc$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateIssueRfcc>(CertificateActionTypes.CertificateIssueRfcc),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource<HttpResponse<void>>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'issueRfcc',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.data],
        onSuccess: () => null,
      }),
    ),
  );

  moveMccToHandover$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateMoveMccToHandover>(CertificateActionTypes.CertificateMoveMccToHandover),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource<void>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'moveMccToHandover',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.data],
        onSuccess: () => new CertificateRfccIssueOptionsLoad(),
      }),
    ),
  );

  issueRfoc$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateIssueRfoc>(CertificateActionTypes.CertificateIssueRfoc),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource<HttpResponse<void>>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'issueRfoc',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.data],
        // Also refresh the punch-round certificate view and the punch rounds
        // themselves (CSA rfoCertificateIssued flags live there) — a harmless
        // no-op when punch-round mode isn't in use, but required to reflect
        // newly-issued RFOCs that were built from punch-round CP-level check-sheets.
        onSuccess: () => [new LoadPunchRoundCertificates(), new LoadPunchRounds()],
      }),
    ),
  );

  loadMccIssueOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateMccIssueOptionsLoad>(CertificateActionTypes.CertificateMccIssueOptionsLoad),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'getMccIssueOptions',
        serviceArgs: ([_, currentProject, cpId]) => [currentProject.id, cpId],
        onSuccess: (response) => new CertificateMccIssueOptionsLoadSuccess(response as any),
      }),
    ),
  );

  loadRfccIssueOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateRfccIssueOptionsLoad>(CertificateActionTypes.CertificateRfccIssueOptionsLoad),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'getRfccIssueOptions',
        serviceArgs: ([_, currentProject, cpId]) => [currentProject.id, cpId],
        onSuccess: (response) => new CertificateRfccIssueOptionsLoadSuccess(response as any),
      }),
    ),
  );

  loadRfocIssueOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateRfocIssueOptionsLoad>(CertificateActionTypes.CertificateRfocIssueOptionsLoad),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'getRfocIssueOptions',
        serviceArgs: ([_, currentProject, cpId]) => [currentProject.id, cpId],
        onSuccess: (response) => new CertificateRfocIssueOptionsLoadSuccess(response as any),
      }),
    ),
  );

  loadCertificateDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateDetailsLoad>(CertificateActionTypes.CertificateDetailsLoad),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'getCertificateDetails',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.certificateType, action.certificateId],
        onSuccess: (response) => new CertificateDetailsLoadSuccess(response as any),
      }),
    ),
  );

  updateMccSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateUpdateMccSignature>(CertificateActionTypes.CertificateUpdateMccSignature),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'updateMccSignature',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.data.certificateId, action.data.signing],
        onSuccess: (response: any) => new CertificateDetailsPatch({ signedBy: response.signedBy, signedDate: response.signedDate }),
      }),
    ),
  );

  updateMccAccepted$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateUpdateMccAccept>(CertificateActionTypes.CertificateUpdateMccAcceptance),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'updateMccAccepted',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.data.certificateId, action.data.accepted],
        onSuccess: (response: any) => new CertificateDetailsPatch({ acceptedBy: response.acceptedBy, acceptedDate: response.acceptedDate }),
      }),
    ),
  );

  updateRfccSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateUpdateRfccSignature>(CertificateActionTypes.CertificateUpdateRfccSignature),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'updateRfccSignature',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.data.certificateId, action.data.signing],
        onSuccess: (response) => new CertificateDetailsPatch({ signatureMatrix: response as any }),
      }),
    ),
  );

  updateRfocSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateUpdateRfocSignature>(CertificateActionTypes.CertificateUpdateRfocSignature),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'updateRfocSignature',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.data.certificateId, action.data.signing],
        onSuccess: (response) => new CertificateDetailsPatch({ signatureMatrix: response as any }),
      }),
    ),
  );

  saveCertificateComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateSaveComment>(CertificateActionTypes.CertificateSaveComment),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'saveCertificateComment',
        serviceArgs: ([action, currentProject, cpId]) => [
          currentProject.id,
          cpId,
          action.data.certificateId,
          action.data.certificateType,
          action.data.comment,
        ],
        onSuccess: (response: any) => new CertificateDetailsPatch({ comment: response.comment }),
      }),
    ),
  );

  uploadAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UploadMccAttachment>(CertificateActionTypes.UploadMccAttachment),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'uploadMccAttachment',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.file, action.certificateId],
        onSuccess: () => null,
      }),
    ),
  );

  deleteMccAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteMccFile>(CertificateActionTypes.DeleteMccFile),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'deleteMccAttachment',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.mccId, action.attachmentId],
        onSuccess: () => null,
      }),
    ),
  );

  syncMccWithMips$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SyncMccWithMips>(CertificateActionTypes.SyncMccWithMips),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.certificateService,
        method: 'syncMccWithMips',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.certificateId],
        onSuccess: (certificate: any) => new SyncMccWithMipsSuccess(certificate),
      }),
    ),
  );

  loadPunchRoundCertificates$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadPunchRoundCertificates>(CertificateActionTypes.LoadPunchRoundCertificates),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId)),
      filter(([_, project, cpId]) => !!(project && cpId)),
      trackResource<PunchRoundMcpView[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchRoundService,
        method: 'getPunchRoundCertificates',
        serviceArgs: ([_, project, cpId]) => [project.id, cpId],
        onSuccess: (data) => new LoadPunchRoundCertificatesSuccess(data),
      }),
    ),
  );

  issuePunchRoundMcCertificate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<IssuePunchRoundMcCertificate>(CertificateActionTypes.IssuePunchRoundMcCertificate),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchRoundService,
        method: 'issuePunchRoundMcCertificate',
        serviceArgs: ([action, project]) => [project.id, action.mcpId, action.roundIds],
        onSuccess: (cert: any) => [
          new IssuePunchRoundMcCertificateSuccess(cert),
          new LoadPunchRoundCertificates(), // re-fetch the full view
        ],
      }),
    ),
  );
}
